import { useState, useCallback } from 'react';

/**
 * Hook para manejar el carrito de productos de una nueva orden
 */
const useCarrito = (usuario, totales, setTotales) => {
    const [cartItems, setCartItems] = useState([]);

    /**
     * Calcula los totales cuando se modifica el carrito
     */
    const calculoTotales = useCallback((items) => {
        var _dTotal = 0;
        var _subtotal = 0;
        var _iva = 0;

        items.forEach((p) => {
            _dTotal = _dTotal + p.dTotal;
            _subtotal = _dTotal / 1.16;
            _iva = _subtotal * 0.16;
        });

        setTotales(prevTotales => ({
            ...prevTotales,
            dSubtotal: _subtotal,
            dDescuento: 0.00,
            dIVA: _iva,
            dTotal: _dTotal
        }));
    }, [setTotales]);

    /**
     * Agrega un producto al carrito
     */
    const agregarProductoAlCarrito = useCallback((producto, tipoRenovacion = null) => {
        const nuevoProducto = {
            index: cartItems.length + 1,
            idProducto: producto.idProducto,
            vCodigoBarras: producto.vCodigoBarras,
            vNombreProducto: producto.vNombreProducto,
            dPrecioUnitario: tipoRenovacion ? tipoRenovacion.dPrecioUnitario : producto.dPrecioUnitario,
            dCantidad: 1,
            dImporte: tipoRenovacion ? tipoRenovacion.dPrecioUnitario : producto.dPrecioUnitario,
            dDescuento: 0,
            dIVA: tipoRenovacion ? tipoRenovacion.dIVA : producto.dIVA,
            dTotal: tipoRenovacion ? tipoRenovacion.dTotal : producto.dTotal,
            vCreatedUser: usuario,
            vClaveUnidad: 'H87',
            bEdit: false,
            vTipoProducto: producto.vTipoProducto,
            iTipoPago: producto.iTipoPago,
            // Agregar información del tipo de renovación si aplica
            ...(tipoRenovacion && {
                idProductoRenovaciones: tipoRenovacion.idProductoRenovaciones,
                vTipoRenovacion: tipoRenovacion.vTipoRenovacion,
                dTasaCuotaIVA: tipoRenovacion.dTasaCuotaIVA,
                vTipoFactorIVA: tipoRenovacion.vTipoFactorIVA
            })
        };

        const nuevosItems = [...cartItems, nuevoProducto];
        setCartItems(nuevosItems);
        calculoTotales(nuevosItems);
    }, [cartItems, usuario, calculoTotales]);

    /**
     * Elimina un producto del carrito
     */
    const eliminarProducto = useCallback((index) => {
        const nuevosItems = cartItems.filter(a => a.index !== index);
        setCartItems(nuevosItems);
        calculoTotales(nuevosItems);
    }, [cartItems, calculoTotales]);

    /**
     * Actualiza un producto del carrito
     */
    const actualizarProducto = useCallback((index, cantidad, precioUnitarioIVA) => {
        const nuevosItems = cartItems.map(a => {
            if (a.index === index) {
                let _dTotal = 0, _dSubtotal = 0, _dIVA = 0, _dCantidad = 0, _dPrecioUnitario = 0;

                // Convertir y validar los valores
                const cantidadNum = parseInt(cantidad, 10);
                const precioNum = parseFloat(precioUnitarioIVA);

                if (Number.isNaN(precioNum) || Number.isNaN(cantidadNum)) {
                    _dTotal = 0;
                    _dIVA = 0;
                    _dSubtotal = 0;
                    _dCantidad = 0;
                    _dPrecioUnitario = 0;
                } else {
                    _dCantidad = cantidadNum;
                    _dTotal = precioNum * _dCantidad;
                    _dSubtotal = _dTotal / 1.16;
                    _dIVA = _dSubtotal * 0.16;
                    _dPrecioUnitario = precioNum / 1.16;
                }

                return { 
                    ...a, 
                    bEdit: false, 
                    dTotal: _dTotal, 
                    dIVA: _dIVA, 
                    dPrecioUnitario: _dPrecioUnitario, 
                    dImporte: _dSubtotal, 
                    dCantidad: _dCantidad 
                };
            }
            return a;
        });

        setCartItems(nuevosItems);
        calculoTotales(nuevosItems);
    }, [cartItems, calculoTotales]);

    /**
     * Activa/desactiva el modo edición de un producto
     */
    const toggleEditarProducto = useCallback((index, editar) => {
        setCartItems(prevItems =>
            prevItems.map(a => {
                if (a.index === index) {
                    return { ...a, bEdit: editar };
                }
                return a;
            })
        );
    }, []);

    return {
        cartItems,
        setCartItems,
        agregarProductoAlCarrito,
        eliminarProducto,
        actualizarProducto,
        toggleEditarProducto,
        calculoTotales
    };
};

export default useCarrito;
